<script context="module" lang="ts">
  import type { Options } from '@splidejs/splide';
  import { Splide, SplideSlide } from '@splidejs/svelte-splide';
  import { getAppConfig } from '../../../common/config/app-config';
  import '../../../common/i18n/i18n';

  const isRTL = isDirectionRTL();

  const thumbsOptions: Options = {
    type: 'slide',
    arrows: true,
    rewind: false,
    pagination: false,
    perPage: 4,
    perMove: 1,
    autoWidth: true,
    autoHeight: true,
    releaseWheel: true,
    cover: true,
    updateOnMove: true,
    direction: (isRTL && 'rtl') || 'ltr',
    breakpoints: {
      900: {
        arrows: false,
        width: '92vw',
        perPage: 1,
      },
    },
    classes: {
      arrow: 'splide__arrow splide_arrow-bg arrows-bg',
      prev: 'splide__arrow--prev splide_arrow-prev',
      next: 'splide__arrow--next splide_arrow-next',
    },
  };
</script>

<script lang="ts">
  import { onMount } from 'svelte';
  import { algoliaSearchData } from '../../../common/store/algolia-config-store';
  import { reorderProductsWithConfig } from '../../../common/store/mirakl-ads-store';
  import {
    getProductDataForGTM,
    getStr,
    isDirectionRTL,
    isMobile,
    isPointWithinElementBounds,
  } from '../../../common/util';
  import MiraklSponsoredTag from '../../common/components/MiraklSponsoredTag.svelte';
  import ProductTileMain from '../../common/components/productTile/ProductTileMain.svelte';
  import RMNProductCard from '../../common/components/RMNProductCard.svelte';
  import { getRRTemplateData } from '../../common/components/rrTemplateData.js';
  import SponsoredTag from '../../common/components/SponsoredTag.svelte';
  import { getCartLoginDetails } from '../../header/services/header-service';
  import { addToWishlist } from '../services/home-data-services';
  import RecommendationProductTileWithIndex from './Recommendation/component/RecommendationProductTileWithIndex.svelte';
  import RecommendationTitle from './Recommendation/component/RecommendationTitle.svelte';

  export let pageData: any;
  export let isProductCrousel: boolean = false;
  export let ProductCrouselData: any;
  export let isDealTimer: boolean = false;
  export const rank = null;
  export let rrType = null;
  export let carouselOptions = {};
  export let isSponsored = false;
  export let isRRComponent = false;
  export let treat3pItemsAsSponsored = false;

  $: mergedCarouselOptions = Object.assign({}, thumbsOptions, carouselOptions);

  const mobile = isMobile();

  const {
    lang = 'en',
    encodedContextPath = 'en-sa',
    countryCode,
    siteBaseUrl,
    pageId,
    miraklads: { miraklAdsPdpNeflixThreshold, miraklAdsPdpPositions },
  } = getAppConfig();

  let rrBlockData = null;

  $: {
    if (!isProductCrousel && !isDealTimer) {
      const data = $algoliaSearchData.find(item => item.key === pageData.key);
      if (data) {
        rrBlockData = getRRTemplateData(data, lang, countryCode);
      } else {
        rrBlockData = null;
      }
    } else {
      rrBlockData = ProductCrouselData;
    }
  }
  // Get the algolia query id from URL
  const params = new Proxy(new URLSearchParams(window.location.search), {
    get: (searchParams, prop) => searchParams.get(prop),
  });

  const uiExperienceLevelQueryParams = params.uiExperienceLevel;

  function onClickWishlist(data, productCode) {
    data.detail.event.stopPropagation();
    data.detail.event.preventDefault();
    if (uiExperienceLevelQueryParams) {
      window.location.href = siteBaseUrl + '/wishlistapp?sku=' + productCode;
      return false;
    }
    // addToWishlist
    addToWishlist(productCode)
      .then(res => {
        if (res.success === false) {
          window.location.assign(res.redirectUrl);
        } else {
          // add to wishlist success, get new cartDetails
          getCartLoginDetails();
          document.querySelector('.js-wishlist-count').innerHTML = res.wishlistSize;
        }
      })
      .catch(error => {
        console.log(error);
      })
      .finally(() => window['ACC'].loader.hideLoader());
  }

  function handleRRItemClick(event, rrClickTrackingURL, _href, productData, index) {
    event.preventDefault();

    // check if wishlist icon clicked
    const wishlistWrapper = event.target?.closest('.product-tile-container')?.querySelector('.fav-wrapper');
    if (wishlistWrapper) {
      if (isPointWithinElementBounds(event, wishlistWrapper)) {
        // do nothing
        return false;
      }
    }

    if (productData.miraklad) {
      // its a sponsored item
      productData.miraklad.position = index + 1;
      window.ACC.mixpanel.trackSponsoredPlacementClicked(productData, 'PDP');
    }

    if (navigator.sendBeacon && rrClickTrackingURL) {
      navigator.sendBeacon(rrClickTrackingURL);
    }

    if (!uiExperienceLevelQueryParams) {
      if (productData !== undefined) {
        window['ACC'].extragtm.click.trackRecommendationsClick(productData);
        window['ACC'].mixpanel.trackRecommendationsClick(productData);
      }
      if (pageData?.key === window['ACC'].richrelevance.vars.similarProductsPlacementName) {
        window['ACC'].mixpanel?.track('Similar_items_Clicks', {
          productData,
        });
      }
      if (productData?.isRecommendation) {
        localStorage.setItem('recommendationClicked', 'true');
      }
      window['ACC'].extragtm.click.productClick(
        ProductCrouselData.strategyMessage,
        productData?.name,
        productData?.code,
        productData?.price?.value,
        productData?.rmsCategory,
        productData?.brandName,
        '',
        '',
        productData?.amplienceProductBaseUrl
      );
    }
    window.location.assign(_href);
  }

  onMount(() => {
    const parent = document.querySelectorAll('.rr_parent_item_container');
    if (isRTL && parent && parent.length > 0) {
      parent.forEach(function (section) {
        const nextArrow = section.querySelector('.splide__arrow--next');
        const prvArrow = section.querySelector('.splide__arrow--prev');
        if (nextArrow) {
          nextArrow.style.setProperty('right', 'auto', 'important');
          nextArrow.style.setProperty('left', '1em', 'important');
        }
        if (prvArrow) {
          prvArrow.style.setProperty('left', 'auto', 'important');
          prvArrow.style.setProperty('right', '1em', 'important');
        }
      });
    }
  });

  function getProductCardHref(productData: any): string {
    let href = `${encodedContextPath}${productData.url}`;
    if (productData.miraklad) {
      href += '?sponsored=true';
    }
    return href;
  }
</script>

<div class="c_wrapper {isDealTimer ? `bg_blue` : `bg_white`}">
  <div
    class=" rr_parent_item_container c_wrapper--content_scroller {pageId} {isDealTimer ? `bg_blue` : `bg_white`}"
    class:mobile
    class:product-carousel={isProductCrousel}
    id={pageData.key}
  >
    {#await rrBlockData}<div />
    {:then rrData}
      {#if rrData && pageData?.display && rrData.hits?.length && !('productList' in rrData)}
        {#if rrData?.strategyMessage}
          <h2
            class="rr_item--headline {pageId === 'productDetails' ? 'hide' : ''}"
            class:rtl={isRTL}
            class:with-subtitle={!!rrData?.subtitle}
          >
            {rrData?.strategyMessage}
            {#if isSponsored}
              <MiraklSponsoredTag variant="CAROUSEL_TITLE" />
            {/if}
          </h2>
        {/if}
        {#if rrData?.subtitle}
          <p class="rr_item--subheading">{rrData.subtitle}</p>
        {/if}
        {#if pageId === 'productDetails' && rrData?.hits?.length <= miraklAdsPdpNeflixThreshold}
          <section class="title-section">
            <RecommendationTitle text={getStr('extra.pdp.rmn.netflix.title')} />
          </section>
          <RecommendationProductTileWithIndex
            tileData={reorderProductsWithConfig(rrData.hits, miraklAdsPdpPositions)}
            showTileIndex={true}
          />
        {/if}
        <Splide
          options={{
            ...mergedCarouselOptions,
          }}
        >
          {#if rrData && rrData.hits}
            {#each reorderProductsWithConfig(rrData.hits, miraklAdsPdpPositions) as productData, i}
              <SplideSlide>
                <a
                  title={productData.productName}
                  href={getProductCardHref(productData)}
                  class="link"
                  data-product-id={productData.code}
                  on:click|preventDefault|stopPropagation={e =>
                    handleRRItemClick(
                      e,
                      productData.rrClickTrackingURL || productData.rrClickUrl,
                      `${encodedContextPath}${productData.url}`,
                      productData,
                      i
                    )}
                >
                  {#if pageId === 'productDetails' && rrData?.hits?.length > miraklAdsPdpNeflixThreshold}
                    <RMNProductCard
                      data={productData}
                      index={i}
                      {treat3pItemsAsSponsored}
                      options={{
                        desktopTileWidth: 255,
                      }}
                    />
                  {:else if pageId !== 'productDetails'}
                    <ProductTileMain
                      data={productData}
                      on:toggleFav={data => {
                        onClickWishlist(data, productData.code);
                      }}
                      productDataForGTM={getProductDataForGTM(productData, i)}
                      options={{ isRRComponent: isRRComponent }}
                    />
                  {/if}
                </a>
              </SplideSlide>
            {/each}
          {/if}
        </Splide>
      {:else if rrData.productList && rrData.productList.length > 0}
        {#if rrData?.message}
          <h2 class="rr_item--headline" class:rtl={isRTL} class:with-subtitle={!!rrData?.subtitle}>
            {rrData?.message}
            {#if isSponsored}
              <SponsoredTag variant="product-carousal" />
            {/if}
          </h2>
        {/if}
        {#if rrData?.subtitle}
          <p class="rr_item--subheading">{rrData.subtitle}</p>
        {/if}
        <Splide options={{ ...mergedCarouselOptions }}>
          {#each rrData.productList as productData, i}
            <SplideSlide>
              <a
                title={productData.name}
                class="link"
                href={getProductCardHref(productData)}
                data-product-id={productData.code}
                on:click|preventDefault|stopPropagation={e =>
                  handleRRItemClick(
                    e,
                    productData.rrClickUrl || productData.rrClickTrackingURL,
                    `${encodedContextPath}${productData.url}`,
                    productData,
                    i
                  )}
              >
                {#if pageId === 'productDetails'}
                  <RMNProductCard
                    data={productData}
                    {treat3pItemsAsSponsored}
                    options={{
                      desktopTileWidth: 255,
                    }}
                  />
                {:else}
                  <ProductTileMain
                    data={productData}
                    options={{ showActionBtn: false, isRRComponent: isRRComponent }}
                    on:toggleFav={data => {
                      onClickWishlist(data, productData.code);
                    }}
                    productDataForGTM={getProductDataForGTM(productData, i)}
                  />
                {/if}
              </a>
            </SplideSlide>
          {/each}
        </Splide>
      {/if}
    {/await}
  </div>
</div>

<style lang="scss">
  @import '../../../common/styles/vars';
  @import '../../common/styles/colors.new.scss';

  :global {
    .rr_parent_item_container {
      .splide--rtl > .splide__arrows .splide__arrow--prev,
      .splide--rtl > .splide__slider > .splide__track > .splide__arrows .splide__arrow--prev,
      .splide--rtl > .splide__track > .splide__arrows .splide__arrow--prev {
        left: auto !important;
        right: 1em !important;
      }
      .splide--rtl > .splide__arrows .splide__arrow--next,
      .splide--rtl > .splide__slider > .splide__track > .splide__arrows .splide__arrow--next,
      .splide--rtl > .splide__track > .splide__arrows .splide__arrow--next {
        right: auto !important;
        left: 1em !important;
      }
      .splide.splide--slide {
        .splide__track {
          padding-left: 0 !important;
          .splide__list {
            display: flex;
            gap: 4px;
            @include desktop-min-width {
              gap: 24px;
            }
            .splide__slide {
              a {
                // color: unset;  /Comment color for the fix of MAVD-8565
              }
              .link {
                height: 100%;
              }
              .tile-content-wrapper {
                height: 100%;
                display: flex;
                flex-direction: column;
              }
            }
          }
        }
        &.splide--rtl {
          direction: unset;
        }
      }
      .splide__arrow:hover svg {
        fill: $primary-white !important;
      }
      .splide__arrow {
        background: $primary-black !important;
        border-radius: 50%;
        display: none;
        height: 48px;
        opacity: 0;
        width: 48px;
        svg {
          fill: $primary-white;
        }
      }
      .splide__arrow:disabled {
        opacity: 0;
        pointer-events: none;
      }
    }
    .rr_parent_item_container:hover .splide__arrow {
      display: inline-block;
      opacity: 0.7;
    }
    .rr_parent_item_container:hover .splide__arrow:disabled {
      opacity: 0;
      pointer-events: none;
    }
  }

  .bg_white {
    background: $primary-white;
  }

  .bg_blue {
    background: $icon-only-icon-blue;
  }
  .productDetails {
    background-color: $neutral-light-04;
    margin-top: 64px;
    padding: 16px 10px;
  }
  .rr_ {
    &wrapper--content {
      padding: 40px 0 40px 16px;
      @include desktop-min-width {
        padding: 80px 0 80px 0;
      }
    }
    &item--headline {
      font-size: 20px;
      font-weight: 700;
      line-height: 22px;
      color: $neutral-dark-01;
      text-align: center;
      padding: 0;
      margin-bottom: 0;
      display: flex;
      margin-top: 0;
      justify-content: unset;
      align-items: baseline;

      @include desktop-min-width {
        font-size: 36px;
        line-height: 40px;
        text-align: left;
      }

      &.with-subtitle {
        margin-bottom: 4px;

        @include desktop-min-width {
          margin-bottom: 8px;
        }
      }
    }
    &item--subheading {
      margin: 0 0 16px;
      padding: 0;
      font-size: 14px;
      font-weight: 400;
      line-height: 16px;
      color: $neutral-dark-02;

      @include desktop-min-width {
        margin: 0 0 20px;
        font-size: 18px;
        line-height: 20px;
      }
    }
    &parent_item_container {
      width: 100%;
      max-width: 1440px;
      padding: 40px 16px;
      display: flex;
      row-gap: 20px;
      flex-direction: column;
      @include desktop-min-width {
        padding: 80px 48px;
        row-gap: 20px;
      }
      a {
        text-decoration: none;
      }
      .splide__arrow {
        background: $primary-black;
        display: none;
        height: 48px;
        opacity: 0;
        width: 48px;
        svg {
          fill: $primary-white;
        }
      }
    }
  }
</style>
